import React from "react";

import { Tooltip } from "antd";
import { Global, css } from "@emotion/react";

import { useAttachmentValidation } from "./AttachmentValidationContext";
import { Button } from "shared/components/Button";
import ErrorBadge from "../ErrorBadge";

const errorTooltipStyles = css`
  .error-tooltip .ant-tooltip-inner {
    background-color: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    max-width: 280px;
    color: #2d2943;
    overflow: hidden;
  }
  .error-tooltip .ant-tooltip-arrow::before {
    background-color: #fff;
    border: 1px solid #e5e7eb;
  }
`;

const ErrorBadgeButton = ({
  isSubmitting,
  dirty,
  disabled: externalDisabled,
  showBadge = true,
}) => {
  const { warnings } = useAttachmentValidation();
  // Warnings are only produced by the grid view. When the legacy view is
  // active (flag off), `showBadge` is false and the badge/tooltip/disabled
  // state are suppressed so the legacy UI behaves like before.
  const hasWarnings = showBadge && warnings.length > 0;

  const warningLinks = warnings.map((w) => ({
    label: `${w.sectionTitle} — ${w.persona}`,
    sectionId: w.sectionId,
    personaKey: w.personaKey,
    key: `${w.sectionId}-${w.personaKey}`,
  }));

  return (
    <>
      <Global styles={errorTooltipStyles} />
      <Tooltip
        overlayClassName="error-tooltip"
        placement="top"
        title={
          hasWarnings ? (
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 10px",
                  background: "#FEE2E2",
                  fontWeight: 600,
                  fontSize: 11,
                  color: "#DC2626",
                }}
              >
                <span style={{ fontSize: 12 }}>&#9888;</span>
                {warningLinks.length}{" "}
                {warningLinks.length === 1 ? "error" : "errors"} to resolve
              </div>
              <div style={{ padding: "6px 10px" }}>
                {warningLinks.map((link, i) => (
                  <div
                    key={link.key}
                    style={{
                      padding: "4px 0",
                      borderTop: i > 0 ? "1px solid #F3F4F6" : "none",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        const el = document.getElementById(
                          link.sectionId || ""
                        );
                        if (el) {
                          el.dispatchEvent(
                            new CustomEvent("focus-error-tab", {
                              detail: { personaKey: link.personaKey },
                            })
                          );
                          el.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }
                      }}
                      style={{
                        cursor: "pointer",
                        color: "#2D2943",
                        fontWeight: 500,
                        fontSize: 12,
                        background: "none",
                        border: "none",
                        padding: 0,
                        textDecoration: "none",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.textDecoration = "underline";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = "none";
                      }}
                    >
                      {link.label}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : undefined
        }
      >
        <span style={{ position: "relative", display: "inline-block" }}>
          {/* @ts-ignore */}
          <Button
            id="form-submit"
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            disabled={!dirty || externalDisabled || hasWarnings}
            data-testid="submit-button"
          >
            Save changes
          </Button>
          {hasWarnings && (
            <ErrorBadge
              style={{
                position: "absolute",
                top: -6,
                right: -6,
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              }}
            >
              {warningLinks.length}
            </ErrorBadge>
          )}
        </span>
      </Tooltip>
    </>
  );
};

export default ErrorBadgeButton;
